import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
  fetchCompanyActivationList,
  fetchUpdateCompanyLicenceStatus,
} from 'modules/companyActivation/actions';
import { companyStatusEnum } from 'modules/companyActivation/enums';
import { fileTypeEnum } from 'modules/common/enums';
import { readPreSignedUrl } from 'modules/common/api';
import CompanyActivationComponent from 'modules/companyActivation/components/CompanyActivationComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const CompanyActivationContainer = ({ companyActivationListApiCallBody }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const { companyActivationList } = useSelector(state => ({
    companyActivationList: state.companyActivation.companyActivationList,
  }));

  const dispatch = useDispatch();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [companyActivationListApiCallParams, setCompanyActivationListApiCallParams] = useState(
    companyActivationListApiCallBody,
  );
  const [showCopiedTextOverlay, setShowCopiedTextOverlay] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPageFilter, setSelectedPageFilter] = useState(1);
  const [isPreviewFilePopupOpen, setIsPreviewFilePopupOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false);
  const [isActionPopupLoading, setIsActionPopupLoading] = useState(false);
  const [actionPopupStatus, setActionPopupStatus] = useState('');
  const [actionPopupTarget, setActionPopupTarget] = useState('');
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const submitSearchQuery = (reset, value) => {
    setSelectedPageFilter(1);

    const body = {
      data: {
        ...companyActivationListApiCallParams,
      },
    };

    if (value) body.data.searchKeyword = value;
    if (reset) delete body.data.searchKeyword;

    setCompanyActivationListApiCallParams(body.data);
    dispatch(fetchCompanyActivationList(body));
  };

  const reloadCompanyActivationPage = () => {
    const totalResultsCount = companyActivationList?.details?.totalCount;
    const numberOfPages = (totalResultsCount && Math.ceil((totalResultsCount - 1) / 10)) || 1;

    const data = {
      ...companyActivationListApiCallParams,
    };

    if (selectedPageFilter > numberOfPages) {
      const previousPage = selectedPageFilter - 1;
      setSelectedPageFilter(previousPage);
      data.Page = previousPage;
    } else data.Page = selectedPageFilter;

    const body = {
      data,
    };

    dispatch(fetchCompanyActivationList(body));
  };

  const actionPopupConfirmBtnCallback = shouldReloadCompanyActivation => {
    setIsActionPopupOpen(false);
    setIsActionPopupLoading(false);
    setActionPopupStatus('');
    setActionPopupTarget('');

    if (shouldReloadCompanyActivation) reloadCompanyActivationPage();
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleCompanyStatusFilterClick = selectedStatus => {
    setSelectedStatusFilter(selectedStatus);
    setSelectedPageFilter(1);

    const body = {
      data: {
        ...companyActivationListApiCallParams,
      },
    };
    if (selectedStatus) {
      body.data.LicenceStatus = companyStatusEnum.getKeyByValue(selectedStatus);
    } else {
      delete body.data.LicenceStatus;
    }

    setCompanyActivationListApiCallParams(body.data);

    dispatch(fetchCompanyActivationList(body));
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;
    const searchQueryMinLength = 3;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        submitSearchQuery(false, value);
      }, 300);
    }
    // this condition checks if we are deleteing letters from search query and we reached
    // 2 characters, then we need to clear search query filter
    else if (searchQuery.length >= searchQueryMinLength && value.length < searchQueryMinLength)
      submitSearchQuery(true);
  };

  const handleCompanyPaginationClick = selectedPageNumber => {
    setSelectedPageFilter(selectedPageNumber);

    const data = {
      ...companyActivationListApiCallParams,
    };
    data.Page = selectedPageNumber;
    const body = {
      data,
    };

    dispatch(fetchCompanyActivationList(body));
  };

  const handleTextToCopyToClipboardClick = textToCopy => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopiedTextOverlay(true);

    setTimeout(() => {
      setShowCopiedTextOverlay(false);
    }, 1000);
  };

  const handlePreviewFileClick = (openPopup, fileUrl, licenceType) => {
    if (openPopup) {
      const body = {
        headers: {
          ServiceKey: 'dn-licence',
          'x-vzt-Component': 'doctornetwork protal',
        },
        data: {
          FilePaths: fileUrl,
        },
      };
      readPreSignedUrl(body).then(response => {
        const url = response.data.data[0].presignedURL;
        if (licenceType === fileTypeEnum.pdf) window.open(url);
        else {
          setPreviewFileUrl(url);
          setIsPreviewFilePopupOpen(true);
        }
      });
    } else setIsPreviewFilePopupOpen(false);
  };

  const handleActionBtnClick = (companyProfileKey, actionStatus) => {
    setIsActionPopupOpen(true);
    setActionPopupStatus(actionStatus);
    setActionPopupTarget(companyProfileKey);
  };

  const handleActionPopupConfirmBtnClick = () => {
    setIsActionPopupLoading(true);

    const body = {
      data: {
        LicenceStatus: actionPopupStatus,
      },
      companyProfileKey: actionPopupTarget,
      actionPopupCallbackAction: actionPopupConfirmBtnCallback,
    };

    dispatch(fetchUpdateCompanyLicenceStatus(body));
  };

  const handleActionPopupDismissBtnClick = () => {
    setIsActionPopupOpen(false);
    setActionPopupStatus('');
    setActionPopupTarget('');
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <CompanyActivationComponent
      showCopiedTextOverlay={showCopiedTextOverlay}
      selectedStatusFilter={selectedStatusFilter}
      searchQuery={searchQuery}
      selectedPageFilter={selectedPageFilter}
      isPreviewFilePopupOpen={isPreviewFilePopupOpen}
      previewFileUrl={previewFileUrl}
      isActionPopupOpen={isActionPopupOpen}
      isActionPopupLoading={isActionPopupLoading}
      actionPopupStatus={actionPopupStatus}
      handleTextToCopyToClipboardClick={handleTextToCopyToClipboardClick}
      handleCompanyStatusFilterClick={handleCompanyStatusFilterClick}
      handleSearchBarInputChange={handleSearchBarInputChange}
      handleCompanyPaginationClick={handleCompanyPaginationClick}
      handlePreviewFileClick={handlePreviewFileClick}
      handleActionBtnClick={handleActionBtnClick}
      handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
      handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

CompanyActivationContainer.propTypes = {
  companyActivationListApiCallBody: PropTypes.shape({}).isRequired,
};

CompanyActivationContainer.defaultProps = {};

export default CompanyActivationContainer;
